<template>
  <UITagsList
    :dataRefid
    :readonly
    :model-value="instanceTags"
    :list="tagsList"
    @mousedown.stop
    @mouseup.stop
    @update:model-value="handleUpdateDocumentTags"
    @plus:click="handleClickPlus"
    @item:rename="handleRenameTag"
  />
</template>

<script setup lang="ts">
import { computed, markRaw } from 'vue'

import { DocumentClass } from '..'
import { CommonTag } from '@types'

import { handleCatchedError } from '@/helpers/common'
import { NOTIFICATION_DELAY } from '@/const/common'
import { DOCUMENT_FIELD } from '../utils/const'

import { useNotifications } from '@/plugins/notification'
import { useModalsStore } from '@/store/modals'
import { useDocumentsTagsStore } from '@/store/documents/tags'

import { UITagsList } from '@ui/core'
import DocumentFormTagsDialog from './DocumentFormTagsDialog.vue'

type Props = {
  instance: DocumentClass
  readonly?: boolean
  dataRefid?: string
}

const props = defineProps<Props>()

const { progress, remove, update } = useNotifications()

const documentsTagsStore = useDocumentsTagsStore()
const modalsStore = useModalsStore()

const tagsList = computed(() => documentsTagsStore.getList)

const instanceTags = computed(() => {
  const tags = props.instance.field<string[]>(DOCUMENT_FIELD.TAGS).value
  if (!tags) return []
  return tagsList.value.filter(item => tags.includes(item.id as string))
})

const handleUpdateDocumentTags = (newTagsList: CommonTag[]) => {
  props.instance.field(DOCUMENT_FIELD.TAGS).value = newTagsList.map(
    tag => tag.id as string,
  )
}

const handleClickPlus = () => {
  const modalInstance = modalsStore.init(
    'document-add-tag',
    markRaw(DocumentFormTagsDialog),
  )
  modalInstance?.open(modalsStore.getZIndex(), {
    instance: props.instance,
  })
}

const handleRenameTag = async (id: string, name: string) => {
  const nid = await progress({
    message: 'Updating tag',
  })
  try {
    const result = await documentsTagsStore.update({
      id,
      name,
    })
    if (nid) {
      await update(
        nid,
        {
          type: 'success',
          message: `Tag ${result?.name} updated`,
        },
        NOTIFICATION_DELAY,
      )
    }
  } catch (e) {
    if (nid) await remove(nid)
    handleCatchedError(e as string, { id, name })
  }
}
</script>
